import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchComments, createComment, toggleCommentLike, deleteComment, clearComments } from '../../slices/commentsSlice';
import { fetchTopicDetail } from '../../slices/topicsSlice';
import { submitReport } from '../../slices/reportsSlice';
import { styles } from './TopicStyles';
import { updatePoints } from '../../slices/authSlice';

// 🚩 이 함수를 추가해줘야 에러가 사라집니다!
const getToxicityColor = (score) => {
  if (score > 0.7) return '#ff4e50'; // 위험 (레드)
  if (score > 0.4) return '#ffd700'; // 주의 (골드)
  return '#00d4ff'; // 청정 (블루)
};

const CommentSection = ({ postId, user, topicId }) => {

  // complete(const 3개)
  const dispatch = useDispatch();
  const comments = useSelector((state) => state.comments.list) || [];
  const [commentText, setCommentText] = useState('');
  
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportTargetId, setReportTargetId] = useState(null);

  const REPORT_REASONS = [
    "스팸/광고성 글",
    "욕설/비방/혐오 표현",
    "개인정보 노출",
    "주제와 무관한 내용",
    "허위 사실",
    "소속 진영과 안 맞는 내용",
    "기타"
  ];

  // [추가] 댓글 좋아요 처리 함수
  // complete(handleCommentLike)
  const handleCommentLike = (commentId, targetFaction) => {
    if (user?.currentFaction !== targetFaction) {
      alert(`${targetFaction === 'BLUE' ? '블루' : '레드'} 진영 유저만 누를 수 있습니다!`);
      return;
    }
    dispatch(toggleCommentLike({ commentId }))
      .unwrap()
      .then(() => { dispatch(fetchTopicDetail(topicId)); })
      .catch((err) => {
        console.error("댓글 좋아요 처리 실패:", err);
        alert("댓글 좋아요 처리에 실패했습니다: " + (err?.message || "서버 오류"));
      });
  };

  // 댓글 삭제 처리 함수. 게시글 댓글 수 최신화는 postSlice.js의 addmatcher에서.
  // complete(handleCommentDelete)
  const handleCommentDelete = (commentId) => {
    if (!window.confirm("이 분석 데이터를 삭제하시겠습니까?")) return;
    dispatch(deleteComment({commentId}))
        .unwrap()
        .then(() => { dispatch(fetchTopicDetail(topicId)); })
        .catch((err) => {
          console.error("댓글 삭제 실패:", err);
          alert("댓글 삭제에 실패했습니다: " + (err?.message || "서버 오류"));
        });
  };

  const handleReportSubmit = (reason) => {
    dispatch(submitReport({
      targetType: 'Comment',
      targetId: reportTargetId,
      reason: reason
    })).unwrap()
    .then((res) => {
      if (res.updatedPoints !== undefined) {
        dispatch(updatePoints({ points: res.updatedPoints }));
      }

      alert("신고가 접수되었습니다.");
      setShowReportModal(false);
    })
    .catch(err => alert(err.message || "이미 신고한 콘텐츠입니다."));
  };
  
  // complete(useEffect)
  useEffect(() => {
    if (postId)
      // 댓글 목록을 불러올 때마다 기존 댓글 초기화
      dispatch(clearComments());
      dispatch(fetchComments(postId));
  }, [postId, dispatch]);

  // complete(handleCommentSubmit)
  const handleCommentSubmit = () => {
    if (!commentText.trim()) return;
    if (!user) return alert("로그인이 필요합니다.");
    
    const token = localStorage.getItem('token');
    
    // 전송할 데이터 구성
    const commentData = {
      topicId: topicId,  // 현재 컴포넌트가 인자로 받은 topicId
      postId: postId,    // 현재 컴포넌트가 인자로 받은 postId
      content: commentText.trim(),
      authorId: user._id,
      nickname: user.nickname,
      faction: user.currentFaction,
      likesDetail: []
    };

    console.log("🔥 댓글 DB 저장 시도:", commentData);

    dispatch(createComment(commentData))
      .unwrap()
      .then(() => {
        setCommentText(''); // 입력창 비우기
        
        // 댓글 작성 후, 게시글 댓글 수 최신화
        dispatch(fetchTopicDetail(topicId)); 
        
        // 추가로 댓글 목록도 최신화하고 싶다면 (필요시)
        // dispatch(fetchComments(postId));
      })
      .catch((err) => {
        console.error("댓글 저장 실패:", err);
        alert("댓글 저장 실패: " + (err?.message || "서버 오류"));
      });
  };

  // complete(return)
  return (
    <div style={styles.commentContainer}>
      <h3 style={styles.commentTitle}>상세 분석 ({comments.length})</h3>
      <div style={styles.commentList}>
        {comments.length === 0 && <p style={{color: '#666', fontSize: '0.9rem'}}>아직 분석된 데이터가 없습니다.</p>}
        {comments.map((c) => (
          <div key={c._id} style={{
            ...styles.commentItem,
            borderLeft: c.faction === 'BLUE' ? '3px solid #00d4ff' : '3px solid #ff4e50'
          }}>
          
            <div style={{display: 'flex', justifyContent: 'flex-start', gap: '8px', marginBottom: '5px'}}>
              <span style={c.faction === 'BLUE' ? styles.blueText : styles.redText}>{c.nickname}</span>

              <span style={{fontSize: '0.8rem', color: '#aaa', marginTop: '4px'}}>{new Date(c.createdAt).toLocaleDateString()}</span>

              <span style={{ 
                  fontSize: '0.8rem',
                  marginTop: '4px',
                  color: getToxicityColor(c.perspectiveScore?.total), // 🚩 종합 점수에 따른 색상 적용
                  fontWeight: 'bold'
                }}>
                  (오염도: {(c.perspectiveScore?.total * 100).toFixed(0)}%)
              </span>
            </div>

            <p style={styles.commentContent}>{c.content}</p>
          
            <div style={styles.commentActions}>
              <span onClick={() => handleCommentLike(c._id, 'BLUE')} style={{cursor:'pointer', color:'#00d4ff', opacity: user?.currentFaction === 'BLUE' ? 1 : 0.3}}>
                💙 {c.likes_blue || 0}
              </span>
              <span onClick={() => handleCommentLike(c._id, 'RED')} style={{cursor:'pointer', color:'#ff4e50', marginLeft:'10px', opacity: user?.currentFaction === 'RED' ? 1 : 0.3}}>
                ❤️ {c.likes_red || 0}
              </span>

              {/* 신고 버튼 */}
              <button style={styles.reportBtn} onClick={() => { setReportTargetId(c._id); setShowReportModal(true); }}>
                🚩 신고
              </button>

              {(c.authorId === (user._id || user.id)) && (
                <button onClick={() => handleCommentDelete(c._id)} style={styles.commentDeleteBtn}>
                  삭제
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div style={styles.commentInputWrapper}>
        <input 
          value={commentText} 
          onChange={(e) => setCommentText(e.target.value)} 
          placeholder="논리적인 반박이나 동의를 남겨주세요..." 
          style={styles.commentInput}
          onKeyPress={(e) => e.key === 'Enter' && handleCommentSubmit()}
        />
        <button onClick={handleCommentSubmit} style={styles.commentBtn}>전송</button>
      </div>

      {/* 신고 모달 */}
      {showReportModal && (
        <div style={styles.reportModalOverlay}>
          <div style={styles.reportModalContent}>
            <h4>신고 사유 선택</h4>
            <ul style={styles.reasonList}>
              {REPORT_REASONS.map(reason => (
                <li key={reason} style={styles.reasonItem} onClick={() => handleReportSubmit(reason)}>
                  {reason}
                </li>
              ))}
            </ul>
            <button style={styles.cancelBtn} onClick={() => setShowReportModal(false)}>취소</button>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default CommentSection;